/**
 * Exercise name matching (spec §8).
 *
 * Nobody types "Flat Barbell Bench Press". Three stages, in order:
 *
 *   1. Alias table        hand-written, deterministic, instant. Where the common cases land.
 *   2. Normalised exact   lowercase, punctuation and spacing stripped.
 *   3. Fuzzy              token overlap plus edit distance.
 *
 * A single confident match is used directly. Several plausible ones produce a numbered
 * question, which the caller resolves via `session/current.pendingConfirmation`. No match
 * at any stage falls through to the LLM (spec §9).
 */
import { CATALOG, catalogRevision, exerciseById, installedAliases } from "./catalog.js";
/**
 * The most candidates worth putting in a numbered question (D-40). Past this the list
 * stops being a question and starts being the catalog, so it is truncated and the caller
 * is told, so it can ask for something more specific instead.
 */
export const MAX_CANDIDATES = 6;
/**
 * Hand-written aliases. This is the only deterministic stage, so the common cases must
 * land here rather than relying on the fuzzy stage to guess well.
 *
 * Where a bare word could mean several movements, it resolves to the one a lifter means
 * by default — "squat" is a back squat, "bench" is a flat barbell bench press — rather
 * than asking a question with an obvious answer (D-39).
 */
const ALIASES = {
    // Chest
    bench: "flat-barbell-bench-press",
    "bench press": "flat-barbell-bench-press",
    "flat bench": "flat-barbell-bench-press",
    bp: "flat-barbell-bench-press",
    "incline bench": "incline-barbell-bench-press",
    "decline bench": "decline-barbell-bench-press",
    /*
     * Added with the incline and decline Smith presses (D-93). Before those existed, each
     * of these phrases matched one bench confidently; the new names share their words, so
     * all five became numbered questions overnight — and "decline bench press" heads the
     * real 2026-09-07 message. An angle with no "smith" is the barbell, as "incline bench"
     * above already says, and a bare "smith bench" is the flat one, as bare "bench" is.
     */
    "incline bench press": "incline-barbell-bench-press",
    "decline bench press": "decline-barbell-bench-press",
    "smith bench": "smith-machine-bench-press",
    "smith bench press": "smith-machine-bench-press",
    "smith press": "smith-machine-bench-press",
    "db bench": "flat-dumbbell-press",
    "dumbbell bench": "flat-dumbbell-press",
    "incline db": "incline-dumbbell-press",
    "incline dumbbell": "incline-dumbbell-press",
    pushup: "push-up",
    pushups: "push-up",
    "press up": "push-up",
    dips: "chest-dips",
    // Back
    deadlift: "conventional-deadlift",
    dl: "conventional-deadlift",
    sumo: "sumo-deadlift",
    row: "barbell-row",
    "bb row": "barbell-row",
    "db row": "single-arm-dumbbell-row",
    "dumbbell row": "single-arm-dumbbell-row",
    "cable row": "seated-cable-row",
    pullup: "pull-up",
    pullups: "pull-up",
    "pull ups": "pull-up",
    pu: "pull-up",
    chinup: "chin-up",
    chinups: "chin-up",
    "lat pulldown": "wide-grip-lat-pulldown",
    pulldown: "wide-grip-lat-pulldown",
    lats: "wide-grip-lat-pulldown",
    /*
     * "Back" here means behind the neck, answered by the user on 2026-09-09 alongside the
     * catalog entry it needs (D-83). Without this the phrase fuzzy-matches the ORDINARY
     * Wide-Grip Lat Pulldown — confidently, silently, and in the same message that also
     * logs the front version, so two different exercises merge into one row and the day's
     * total still looks right. That is the D-78 failure in its exact shape.
     *
     * "behind neck pulldown" needs no alias: the catalog now contains those words and the
     * fuzzy stage finds them on its own (D-77). Only the phrasing the catalog LACKS is
     * here, and it is scoped to phrases carrying both "back" and "pulldown" so nothing
     * else — "back squat", "back extension" — is touched.
     */
    "back lat pulldown": "wide-grip-behind-neck-lat-pulldown",
    "wide grip back lat pulldown": "wide-grip-behind-neck-lat-pulldown",
    /*
     * "row pull down" names both a row and a pulldown, so the matcher asked a numbered
     * question — correct behaviour and the right question. Answered on 2026-09-09: the
     * pulldown. The word "pulldown" in it is the tiebreak, so the alias is scoped to the
     * phrase that carries both rather than loosening "single arm" generally.
     */
    "single arm row pulldown": "single-arm-lat-pulldown",
    // Shoulders
    ohp: "standing-barbell-overhead-press",
    "overhead press": "standing-barbell-overhead-press",
    "military press": "standing-barbell-overhead-press",
    "shoulder press": "seated-dumbbell-shoulder-press",
    "lateral raise": "lateral-side-raise",
    "side raise": "lateral-side-raise",
    laterals: "lateral-side-raise",
    "rear delt": "dumbbell-rear-delt-fly",
    shrugs: "barbell-shrug",
    /*
     * "Back shrug" matched nothing — the catalog says Barbell and Dumbbell, never "back".
     * The user answered on 2026-09-09 that theirs is the dumbbell one, which is also what
     * the 2.5/5kg loads say. Bare "shrugs" still means the barbell, as it always has.
     */
    "back shrug": "dumbbell-shrug",
    "back shrugs": "dumbbell-shrug",
    "db shrug": "dumbbell-shrug",
    // Arms
    curl: "barbell-curl",
    curls: "barbell-curl",
    "bicep curl": "barbell-curl",
    "db curl": "standing-dumbbell-curl",
    "dumbbell curl": "standing-dumbbell-curl",
    hammers: "hammer-curl",
    skullcrusher: "ez-bar-skull-crusher",
    skullcrushers: "ez-bar-skull-crusher",
    pushdown: "rope-tricep-pushdown",
    pushdowns: "rope-tricep-pushdown",
    "tricep extension": "dumbbell-overhead-tricep-extension",
    cgbp: "close-grip-bench-press",
    // Legs
    squat: "back-squat",
    squats: "back-squat",
    bs: "back-squat",
    "front squats": "front-squat",
    rdl: "barbell-romanian-deadlift",
    rdls: "barbell-romanian-deadlift",
    "romanian deadlift": "barbell-romanian-deadlift",
    sldl: "stiff-leg-deadlift",
    "leg curl": "lying-leg-curl",
    "leg extensions": "leg-extension",
    lunges: "walking-lunge",
    "split squat": "bulgarian-split-squat",
    bulgarians: "bulgarian-split-squat",
    "hip thrust": "barbell-hip-thrust",
    "calf raise": "standing-machine-calf-raise",
    "calf raises": "standing-machine-calf-raise",
    calves: "standing-machine-calf-raise",
    // Words the catalog does not contain at all (P-06). The catalog calls it a Pec Deck
    // and a Cable Fly; a person writes "pectoral fly" and "cable crossover". A wrong alias
    // is worse than no alias — silent and confident — so these are only the ones a real
    // message asked for. "Cable crossover" resolves to the mid fly, which is the mapping
    // this project's own 2026-06/07 import already used for the same words.
    "pec fly": "pec-deck",
    "pectoral fly": "pec-deck",
    "cable cross": "mid-cable-fly",
    "cable crossover": "mid-cable-fly",
    crossover: "mid-cable-fly",
    /*
     * "Arnold" is a shoulder press everywhere else, and still is on its own. Paired with
     * "concentration" it names neither cleanly — the analysis (P-08) said a parser should
     * ask rather than pick, and the user answered on 2026-09-08: the biceps one. This is
     * an alias because that answer exists, and it is scoped to the phrase carrying both
     * words so nothing else is affected.
     */
    "arnold concentration": "concentration-curl",
    "standing arnold concentration": "concentration-curl",
    /*
     * "cable curl" spans three catalog variants and asked a numbered question, which was
     * the right behaviour and the right question. The user answered it on 2026-09-08: the
     * straight-bar one. Same mapping their 2026-06/07 import already used for those words,
     * so the history stays consistent with itself. Naming a variant still reaches it, and
     * a query that genuinely spans several — "cable fly" — still asks.
     */
    "cable curl": "straight-bar-cable-curl",
    /*
     * "Rope hammer curl" was an alias for the Rope Cable Curl (D-93) until it became an entry
     * of its own (D-116), which its name now finds exactly. "Cable hammer curl" is the same
     * movement and still needs pointing: on "hammer" alone it lands on the DUMBBELL Hammer
     * Curl confidently — the D-76 failure.
     */
    "cable hammer curl": "rope-hammer-curl",
    /*
     * D-117. The catalog's Barbell Row never says "bent over"; once the Close-Grip Bent-Over
     * Barbell Row existed, a plain "bent over row" landed on it confidently — the D-76 failure.
     * Unqualified, it means the ordinary row.
     */
    "bent over row": "barbell-row",
    "bent over rows": "barbell-row",
    "bent over barbell row": "barbell-row",
    "bent over barbell rows": "barbell-row",
    /*
     * D-117. The user's own name for the Reverse-Grip Tricep Pushdown. "Extension" is not in
     * its name, so without this it asked between four extensions and pushdowns.
     */
    "reverse tricep extension": "reverse-grip-tricep-pushdown",
    "reverse tricep extensions": "reverse-grip-tricep-pushdown",
    "reverse triceps extension": "reverse-grip-tricep-pushdown",
    "reverse triceps extensions": "reverse-grip-tricep-pushdown",
    /*
     * "db" is not a word the catalog uses. Before the Incline Cable Fly existed (D-102)
     * "incline db fly" reached the dumbbell fly on "incline" and "fly" alone; with two
     * incline flys it asked, although "db" had already answered. Bare "incline fly" still
     * asks, which is right: it names either one.
     */
    "incline db fly": "incline-dumbbell-fly",
    "incline db flys": "incline-dumbbell-fly",
    "incline db flyes": "incline-dumbbell-fly",
    // Core and conditioning
    planks: "plank",
    situps: "decline-sit-up",
    "sit ups": "decline-sit-up",
    "leg raise": "hanging-leg-raise",
    "leg raises": "hanging-leg-raise",
    abs: "cable-crunch",
    crunches: "cable-crunch",
    burpees: "burpee",
    rower: "rowing-machine",
    erg: "rowing-machine",
    bike: "assault-bike",
    ropes: "battle-ropes",
    // The cardio machines added by D-71. `bike` above still means the assault bike, which
    // is the one that was here first; a stationary cycle has to say so.
    "stationary bike": "cycle",
    "exercise bike": "cycle",
    cycling: "cycle",
    "cross trainer": "elliptical",
    crosstrainer: "elliptical",
    running: "treadmill",
    run: "treadmill",
    jog: "treadmill",
    jogging: "treadmill",
    walk: "treadmill",
    walking: "treadmill",
};
/** Lowercase, punctuation to spaces, whitespace collapsed. "EZ-Bar Curl" → "ez bar curl". */
export function normalise(input) {
    return input
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
}
/** Normalised with spaces removed too. "ez bar curl" → "ezbarcurl". */
function squash(input) {
    return normalise(input).replace(/ /g, "");
}
/*
 * The indexes below are derived from the catalog, which the admin can add to and rename
 * from the portal (B-66). They are rebuilt when `catalogRevision()` moves rather than
 * built once at module load; with nothing installed that is once, as before.
 */
let indexedRevision = -1;
let BY_SQUASHED = new Map();
let ALIAS_BY_NORMALISED = new Map();
/** The same aliases, tokenised, for the near-miss pass that catches a typo in one. */
let ALIAS_TOKENS = [];
function indexes() {
    if (indexedRevision === catalogRevision())
        return;
    // The hand-written table wins over an installed alias for the same phrase: it is the
    // reviewed one, and an admin's "other name" must not silently repoint "bench".
    const all = [...installedAliases(), ...Object.entries(ALIASES)];
    BY_SQUASHED = new Map(CATALOG.map((e) => [squash(e.name), e]));
    ALIAS_BY_NORMALISED = new Map(all.map(([alias, id]) => [squash(alias), id]));
    ALIAS_TOKENS = all.map(([alias, id]) => [normalise(alias).split(" "), id]);
    indexedRevision = catalogRevision();
}
/**
 * Edit distance for catching a typo in a single token — Damerau-Levenshtein, in its
 * optimal-string-alignment form (P-05).
 *
 * Plain Levenshtein scores a transposition as 2, which put it above the threshold
 * `tokenMatches` allows while leaving substitutions at 1. That is backwards: swapping
 * two adjacent letters is the most common typing error there is. "cabel" for "cable" was
 * the miss that found it. Counting an adjacent swap as one edit is one extra branch and
 * the threshold does not move — two separate errors are still two.
 */
export function editDistance(a, b) {
    if (a === b)
        return 0;
    if (a.length === 0)
        return b.length;
    if (b.length === 0)
        return a.length;
    // Three rows rather than two: a transposition looks back two positions in both strings.
    let beforePrevious = [];
    let previous = Array.from({ length: b.length + 1 }, (_, i) => i);
    for (let i = 1; i <= a.length; i++) {
        const current = [i];
        for (let j = 1; j <= b.length; j++) {
            const substitution = previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1);
            let best = Math.min(substitution, previous[j] + 1, current[j - 1] + 1);
            if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
                best = Math.min(best, beforePrevious[j - 2] + 1);
            }
            current[j] = best;
        }
        beforePrevious = previous;
        previous = current;
    }
    return previous[b.length];
}
/** Whether one query token is a plausible reference to one name token. */
function tokenMatches(query, name) {
    if (query === name)
        return true;
    // Plurals, both directions: "curls" ↔ "curl".
    if (query + "s" === name || name + "s" === query)
        return true;
    // Abbreviation by prefix: "barb" → "barbell". Two letters is too little to go on.
    if (query.length >= 3 && name.startsWith(query))
        return true;
    // And the reverse — a query that spells out what the catalog abbreviates:
    // "pectoral" → "pec" (P-06). The same three-character floor, for the same reason.
    if (name.length >= 3 && query.startsWith(name))
        return true;
    // A single typo, but only in a token long enough that one edit is not most of it.
    if (query.length >= 4 && editDistance(query, name) <= 1)
        return true;
    return false;
}
/**
 * How well a query fits one name, or null if it does not plausibly fit at all.
 *
 * An unmatched query token used to be fatal: one word the catalog did not use rejected
 * the name outright, so "hammer curl" matched and "standing hammer curl" did not, and
 * being more specific made the match worse (P-04). It is a penalty now — ranked ahead of
 * `extra`, so a name that uses the word still beats one that merely tolerates it.
 *
 * The floor is a STRICT majority: more than half the query tokens must match. Measured
 * on the 2026-09-07 session — at half, "pectoral fly" matched "fly" alone and produced a
 * six-way question about dumbbell and cable flies, none of them the Pec Deck the sender
 * meant. One word plus a qualifier is too little signal to ask a question about, and a
 * message the matcher cannot name belongs to the LLM (spec §9), not to a wrong question.
 *
 * An unmatched NUMBER is the exception and stays fatal. A word the catalog does not use
 * is a qualifier; a number is not a name. "bench 60" is what a half-read set list looks
 * like, and tolerating it would turn that into a numbered question about every bench in
 * the catalog rather than letting the message reach the LLM. A number the catalog does
 * use — "45 back extension" — is an ordinary token and matches as one.
 */
function fit(queryTokens, name) {
    const nameTokens = normalise(name).split(" ");
    const used = new Set();
    let unmatched = 0;
    for (const query of queryTokens) {
        const index = nameTokens.findIndex((token, i) => !used.has(i) && tokenMatches(query, token));
        if (index === -1) {
            if (/^\d/.test(query))
                return null;
            unmatched += 1;
        }
        else
            used.add(index);
    }
    if (used.size * 2 <= queryTokens.length)
        return null;
    return { unmatched, extra: nameTokens.length - used.size };
}
/**
 * Resolve free text to a catalog exercise.
 *
 * Returns `ambiguous` with candidates in the order they should be numbered, tightest
 * match first, when the text is plausible for more than one movement.
 */
export function matchExercise(input) {
    indexes();
    const squashed = squash(input);
    if (!squashed)
        return { kind: "none" };
    // 1. Alias table.
    const aliasId = ALIAS_BY_NORMALISED.get(squashed);
    if (aliasId) {
        const exercise = exerciseById(aliasId);
        // An alias pointing at an id the catalog no longer has is a bug in this file, not a
        // user error — fall through rather than claim no match exists.
        if (exercise)
            return { kind: "match", exercise, via: "alias" };
    }
    // 2. Normalised exact.
    const exact = BY_SQUASHED.get(squashed);
    if (exact)
        return { kind: "match", exercise: exact, via: "exact" };
    /*
     * 2b. An alias with a typo in it.
     *
     * The alias table was exact-match only, so P-05's transposition tolerance could never
     * reach it — and the aliases are precisely the vocabulary people type, which is where
     * typos land. "cabel cross" needed both fixes and got neither: the alias to know the
     * word "cross", which the catalog does not contain anywhere, and the metric to reach
     * the alias through the swapped letters.
     *
     * Deliberately stricter than the catalog fuzzy stage below: the same token count and
     * EVERY token matching, no penalties and no majority rule. An alias is a short phrase
     * someone chose on purpose, so there is nothing to be lenient about — and a wrong
     * alias is worse than no alias, because it is silent and confident.
     */
    const aliasTokens = normalise(input).split(" ");
    const near = new Set();
    for (const [alias, id] of ALIAS_TOKENS) {
        if (alias.length !== aliasTokens.length)
            continue;
        if (alias.every((token, i) => tokenMatches(aliasTokens[i], token)))
            near.add(id);
    }
    if (near.size === 1) {
        const exercise = exerciseById([...near][0]);
        if (exercise)
            return { kind: "match", exercise, via: "alias" };
    }
    // 3. Fuzzy.
    const queryTokens = normalise(input).split(" ");
    const all = [];
    for (const exercise of CATALOG) {
        const f = fit(queryTokens, exercise.name);
        if (f !== null)
            all.push({ exercise, fit: f });
    }
    if (all.length === 0)
        return { kind: "none" };
    /*
     * A name that accounts for more of the query wins outright, before tightness is
     * considered at all. "standing calf raise" fits Standing Machine Calf Raise with
     * nothing left over and Seated Calf Raise only by ignoring "standing" — so the second
     * one is not a rival, it is a worse reading, and it must not dilute the question.
     */
    const best = Math.min(...all.map((s) => s.fit.unmatched));
    const scored = all.filter((s) => s.fit.unmatched === best);
    if (scored.length === 1)
        return { kind: "match", exercise: scored[0].exercise, via: "fuzzy" };
    // Tightest first; catalog order breaks ties, so the numbering is stable.
    scored.sort((a, b) => a.fit.extra - b.fit.extra);
    // Exactly one candidate that accounts for the whole name is a confident match:
    // "incline dumbbell press" beats "Incline Dumbbell Press" plus its longer cousins.
    const tightest = scored.filter((s) => s.fit.extra === 0);
    if (tightest.length === 1) {
        return { kind: "match", exercise: tightest[0].exercise, via: "fuzzy" };
    }
    return {
        kind: "ambiguous",
        candidates: scored.slice(0, MAX_CANDIDATES).map((s) => s.exercise),
        truncated: scored.length > MAX_CANDIDATES,
    };
}
/** Every alias, for the assumptions view and for tests. */
export function aliasEntries() {
    return Object.entries(ALIASES);
}
//# sourceMappingURL=matcher.js.map